import axios from 'axios';
import { User, Course, Registration, MyRegistration, WithdrawalRequest, CourseChangeRequest, DashboardStats, LoginCredentials, RegisterData, ChangePasswordData, PaymentScreenshot, Order, TeacherRoster, TeacherRosterFilters } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 认证相关API
export const authAPI = {
  login: (credentials: LoginCredentials): Promise<{ token: string; user: User }> =>
    api.post('/auth/login', credentials),
  register: (userData: RegisterData): Promise<{ message: string }> =>
    api.post('/auth/register', userData),
  getProfile: (): Promise<User> =>
    api.get('/auth/me'),
  updateProfile: (userData: { phone: string; realName: string; grade?: string }): Promise<User> =>
    api.put('/auth/profile', userData),
  changePassword: (passwordData: ChangePasswordData): Promise<{ message: string }> =>
    api.put('/auth/password', passwordData),
};

// 课程相关API
export const courseAPI = {
  getCourses: (params?: any): Promise<{ courses: Course[]; pagination: any }> =>
    api.get('/courses', { params }),
  getCourse: (id: number): Promise<Course> =>
    api.get(`/courses/${id}`),
  getCategories: (): Promise<string[]> =>
    api.get('/courses/categories/list'),
  createCourse: (courseData: any): Promise<Course> =>
    api.post('/courses', courseData),
  updateCourse: (id: number, courseData: any): Promise<Course> =>
    api.put(`/courses/${id}`, courseData),
  deleteCourse: (id: number): Promise<{ message: string }> =>
    api.delete(`/courses/${id}`),
  updateCourseStatus: (id: number, status: string): Promise<Course> =>
    api.patch(`/courses/${id}/status`, { status }),
  getCourseRegistrations: (id: number): Promise<Registration[]> =>
    api.get(`/courses/${id}/registrations`),
};

// 报名相关API
export const registrationAPI = {
  getMyRegistrations: (): Promise<MyRegistration[]> =>
    api.get('/registrations/my'),
  registerCourse: (courseId: number): Promise<{ registrationId: number; message: string; paymentQrUrl: string }> =>
    api.post('/registrations', { courseId }),
  adminRegisterCourse: (userId: number, courseId: number): Promise<Registration> =>
    api.post('/registrations/admin-register', { userId, courseId }),
  withdrawCourse: (courseId: number, reason: string): Promise<{ message: string }> =>
    api.post('/registrations/withdraw', { courseId, reason }),
  changeCourse: (originalCourseId: number, targetCourseId: number, reason: string): Promise<{ message: string }> =>
    api.post('/registrations/change-course', {
      originalCourseId,
      targetCourseId,
      reason,
    }),
  cancelRegistration: (id: number): Promise<{ message: string }> =>
    api.delete(`/registrations/${id}`),
  getWithdrawalRequests: (): Promise<WithdrawalRequest[]> =>
    api.get('/registrations/withdrawal-requests'),
  getChangeRequests: (): Promise<CourseChangeRequest[]> =>
    api.get('/registrations/change-requests'),
  processWithdrawalRequest: (id: number, status: string, notes?: string): Promise<{ message: string }> =>
    api.patch(`/registrations/withdrawal-requests/${id}/process`, {
      status,
      notes,
    }),
  processChangeRequest: (id: number, status: string, notes?: string): Promise<{ message: string }> =>
    api.patch(`/registrations/change-requests/${id}/process`, {
      status,
      notes,
    }),
};

// 管理员相关API
export const adminAPI = {
  getDashboard: (): Promise<DashboardStats> =>
    api.get('/admin/dashboard'),
  getUsers: (params?: any): Promise<{ users: User[]; pagination: any }> =>
    api.get('/admin/users', { params }),
  updateUserStatus: (id: number, status: string): Promise<{ message: string }> =>
    api.patch(`/admin/users/${id}/status`, { status }),
  getSystemConfig: (): Promise<{ [key: string]: string }> =>
    api.get('/admin/config'),
  updateSystemConfig: (key: string, value: string): Promise<{ message: string }> =>
    api.put('/admin/config', { key, value }),
  getRecentActivities: (limit?: number): Promise<any[]> =>
    api.get('/admin/recent-activities', { params: { limit } }),
  getCourseStats: (): Promise<any> =>
    api.get('/admin/course-stats'),
  getTeacherRosterFilters: (): Promise<TeacherRosterFilters> =>
    api.get('/admin/teacher-rosters/filters'),
  getTeacherRoster: (params: { teacher: string; semester?: string; includeInactive?: boolean }): Promise<TeacherRoster> =>
    api.get('/admin/teacher-rosters', { params }),
  getTodayOrders: (): Promise<Order[]> =>
    api.get('/admin/orders/today'),
  getAllOrders: (params?: any): Promise<{ orders: Order[]; pagination: any }> =>
    api.get('/admin/orders', { params }),
  updateOrderStatus: (id: number, status: string, notes?: string): Promise<{ message: string }> =>
    api.patch(`/admin/orders/${id}/status`, { status, admin_notes: notes }),
  reviewScreenshot: (id: number, status: string, notes?: string): Promise<{ message: string }> =>
    api.patch(`/admin/screenshots/${id}/status`, { status, admin_notes: notes }),
};

// 优惠券相关API
export const couponAPI = {
  getMyCoupons: (status = 'unused'): Promise<any[]> =>
    api.get(`/coupons?status=${status}`),
  claimCoupon: (couponId: number): Promise<any> =>
    api.post(`/coupons/${couponId}/claim`),
};

// 收藏相关API
export const favoriteAPI = {
  getMyFavorites: (): Promise<any[]> =>
    api.get('/favorites'),
  addFavorite: (courseId: number): Promise<any> =>
    api.post('/favorites', { courseId }),
  removeFavorite: (courseId: number): Promise<any> =>
    api.delete(`/favorites/${courseId}`),
};

// 地址相关API
export const addressAPI = {
  getMyAddresses: (): Promise<any[]> =>
    api.get('/addresses'),
  addAddress: (addressData: any): Promise<any> =>
    api.post('/addresses', addressData),
  updateAddress: (id: number, addressData: any): Promise<any> =>
    api.put(`/addresses/${id}`, addressData),
  deleteAddress: (id: number): Promise<any> =>
    api.delete(`/addresses/${id}`),
  setDefaultAddress: (id: number): Promise<any> =>
    api.put(`/addresses/${id}/default`),
};

// 支付相关API
export const paymentAPI = {
  createPayment: (registrationId: number): Promise<{ qrCode: string; paymentId: string }> =>
    api.post('/payments', { registrationId }),
  getPaymentStatus: (paymentId: string): Promise<{ status: string; paidAt?: string }> =>
    api.get(`/payments/${paymentId}`),
  getMyPayments: (): Promise<any[]> =>
    api.get('/payments/my'),
};

// 支付截图相关API
export const paymentScreenshotAPI = {
  uploadScreenshot: (courseId: number, file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('screenshot', file);
    formData.append('courseId', courseId.toString());
    return api.post('/payment-screenshots/direct-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Legacy method for backward compatibility
  legacyUpload: (registrationId: number, imageData: string): Promise<any> =>
    api.post('/payment-screenshots', { registrationId, imageData }),
  getMyScreenshots: (): Promise<PaymentScreenshot[]> =>
    api.get('/payment-screenshots/my-screenshots'),
  getScreenshot: (id: number): Promise<PaymentScreenshot> =>
    api.get(`/payment-screenshots/${id}`),
  updateScreenshotStatus: (id: number, status: string, notes?: string): Promise<PaymentScreenshot> =>
    api.patch(`/payment-screenshots/${id}/status`, { status, notes }),
  deleteScreenshot: (id: number): Promise<{ message: string }> =>
    api.delete(`/payment-screenshots/${id}`),
};

// 订单相关API
export const ordersAPI = {
  getMyOrders: (): Promise<any[]> =>
    api.get('/orders/my'),
  
  getOrderDetails: (orderId: number): Promise<any> =>
    api.get(`/orders/${orderId}`),
};

export default api;
